import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";

export const WEB_STATS_BRAND = {
  orange: "#F35A3A",
  dark: "var(--wp-panel-text-primary, #19242F)",
  muted: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)",
  surface: "var(--wp-panel-surface, #fff)",
  background: "var(--wp-panel-bg, #F6F7FB)",
  border: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
  green: "#159455",
  blue: "#2F80ED",
  purple: "#7657D5",
  yellow: "#D99B15",
};

export function safeNumber(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatNumber(value, digits = 0) {
  return safeNumber(value).toLocaleString("es-AR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatPercent(value, digits = 1) {
  return `${formatNumber(value, digits)}%`;
}

export function formatDuration(value) {
  const totalSeconds = Math.round(safeNumber(value) / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

export function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatDate(value) {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short" }).format(date);
}

export const CATEGORY_LABELS = {
  navigation: "Navegación",
  commercial: "Acciones comerciales",
  auth: "Accesos",
  user: "Usuarios",
  project: "Configuración",
  landing: "Edición de la web",
  product: "Productos y servicios",
  order: "Pedidos",
  payment: "Pagos",
  appointment: "Agenda",
  erp: "Gestión",
  cash: "Caja",
  stock: "Stock",
  billing: "Suscripción",
  push: "Notificaciones",
  integration: "Integraciones",
  system: "Sistema",
};

export const SOURCE_LABELS = {
  panel: "Panel",
  storefront: "Web pública",
  api: "Operación del sistema",
  system: "Proceso automático",
  webhook: "Integración externa",
  job: "Tarea automática",
};

export function categoryLabel(value) {
  return CATEGORY_LABELS[value] || value || "Sistema";
}

export function sourceLabel(value) {
  return SOURCE_LABELS[value] || value || "Sistema";
}

export function actorLabel(event) {
  const actor = event?.actor || {};
  return actor.name || actor.email || (actor.type === "visitor" ? "Visitante de la web" : actor.type === "customer" ? "Cliente" : actor.type === "webhook" ? "Integración" : "Sistema");
}

export function TrendChip({ comparison, inverse = false, compact = false }) {
  const percent = comparison?.percent;
  if (percent === null || percent === undefined) {
    return (
      <Chip
        size="small"
        icon={<RemoveRoundedIcon />}
        label={compact ? "Sin comparación" : "Sin período anterior comparable"}
        sx={{ bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)", color: WEB_STATS_BRAND.muted, fontWeight: 850 }}
      />
    );
  }
  const up = safeNumber(percent) > 0;
  const favorable = inverse ? !up : up;
  const color = favorable ? WEB_STATS_BRAND.green : WEB_STATS_BRAND.orange;
  return (
    <Chip
      size="small"
      icon={up ? <ArrowUpwardRoundedIcon /> : <ArrowDownwardRoundedIcon />}
      label={`${Math.abs(safeNumber(percent)).toLocaleString("es-AR", { maximumFractionDigits: 1 })}% vs. anterior`}
      sx={{
        bgcolor: favorable ? "rgba(21,148,85,.10)" : "rgba(243,90,58,.10)",
        color,
        fontWeight: 900,
        "& .MuiChip-icon": { color: "inherit" },
      }}
    />
  );
}

export function WebsiteMetricCard({ icon, label, value, helper, comparison, inverseTrend = false, compact = false }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: compact ? 3 : 4, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
      <CardContent
        sx={{
          p: compact ? { xs: 1.15, sm: 1.5 } : 2.4,
          "&:last-child": { pb: compact ? { xs: 1.15, sm: 1.5 } : 2.4 },
        }}
      >
        <Stack direction="row" spacing={compact ? { xs: .75, sm: 1 } : 1.5} alignItems="flex-start">
          <Box
            sx={{
              width: compact ? { xs: 32, sm: 36 } : 44,
              height: compact ? { xs: 32, sm: 36 } : 44,
              borderRadius: compact ? 2.2 : 2.7,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(243,90,58,.10)",
              color: WEB_STATS_BRAND.orange,
              flexShrink: 0,
              "& svg": { fontSize: compact ? { xs: 18, sm: 20 } : 24 },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: "block", color: WEB_STATS_BRAND.muted, fontWeight: 900, lineHeight: 1.2 }}>
              {label}
            </Typography>
            <Typography sx={{ mt: .35, color: WEB_STATS_BRAND.dark, fontWeight: 950, fontSize: compact ? { xs: 18, sm: 21 } : 28, lineHeight: 1.1, overflowWrap: "anywhere" }}>
              {value}
            </Typography>
          </Box>
        </Stack>
        {helper ? <Typography variant="caption" sx={{ display: "block", color: WEB_STATS_BRAND.muted, mt: compact ? .7 : 1, lineHeight: 1.25 }}>{helper}</Typography> : null}
        {comparison ? <Box sx={{ mt: compact ? .8 : 1.15 }}><TrendChip comparison={comparison} inverse={inverseTrend} compact={compact} /></Box> : null}
      </CardContent>
    </Card>
  );
}

export function ActivityChart({ rows = [], compact = false }) {
  if (!rows.length) {
    return <Typography variant="body2" sx={{ color: WEB_STATS_BRAND.muted }}>Todavía no hay movimientos para graficar.</Typography>;
  }
  const width = compact ? 560 : 920;
  const height = compact ? 210 : 280;
  const left = compact ? 12 : 28;
  const right = 12;
  const top = 18;
  const bottom = compact ? 32 : 40;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const max = Math.max(1, ...rows.flatMap((row) => [safeNumber(row.events), safeNumber(row.operations)]));
  const x = (index) => left + (rows.length === 1 ? chartWidth / 2 : (index / (rows.length - 1)) * chartWidth);
  const y = (value) => top + chartHeight - (safeNumber(value) / max) * chartHeight;
  const events = rows.map((row, index) => `${x(index)},${y(row.events)}`).join(" ");
  const operations = rows.map((row, index) => `${x(index)},${y(row.operations)}`).join(" ");
  const labelIndexes = [...new Set([0, Math.floor((rows.length - 1) / 2), rows.length - 1])];

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box sx={{ width: "100%", overflowX: compact ? "auto" : "hidden", WebkitOverflowScrolling: "touch", overscrollBehaviorX: "contain", pb: compact ? .35 : 0 }}>
        <Box sx={{ minWidth: compact ? { xs: 460, sm: 560 } : 0 }}>
          <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Evolución de actividad y operaciones del negocio">
        {[0, .25, .5, .75, 1].map((ratio) => {
          const lineY = top + chartHeight * ratio;
          return <line key={ratio} x1={left} x2={width - right} y1={lineY} y2={lineY} stroke="rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" strokeWidth="1" />;
        })}
        <polyline points={events} fill="none" stroke={WEB_STATS_BRAND.blue} strokeWidth={compact ? 3 : 4} strokeLinecap="round" strokeLinejoin="round" opacity=".7" />
        <polyline points={operations} fill="none" stroke={WEB_STATS_BRAND.orange} strokeWidth={compact ? 3 : 4} strokeLinecap="round" strokeLinejoin="round" />
        {rows.map((row, index) => (
          <circle key={`operation-${row.date}`} cx={x(index)} cy={y(row.operations)} r={compact ? 3 : 4} fill={WEB_STATS_BRAND.orange} />
        ))}
        {labelIndexes.map((index) => (
          <text key={index} x={x(index)} y={height - 8} textAnchor="middle" fontSize={compact ? 18 : 15} fill="rgba(var(--wp-panel-ink-rgb, 25,36,47),.55)">
            {formatDate(rows[index]?.date)}
          </text>
        ))}
          </svg>
        </Box>
      </Box>
      <Stack direction="row" spacing={compact ? 1.2 : 2} justifyContent="center" useFlexGap flexWrap="wrap" sx={{ mt: compact ? .1 : -.5 }}>
        <Stack direction="row" spacing={.55} alignItems="center"><Box sx={{ width: 9, height: 9, borderRadius: 999, bgcolor: WEB_STATS_BRAND.orange }} /><Typography variant="caption" sx={{ fontWeight: 850 }}>Operaciones</Typography></Stack>
        <Stack direction="row" spacing={.55} alignItems="center"><Box sx={{ width: 9, height: 9, borderRadius: 999, bgcolor: WEB_STATS_BRAND.blue }} /><Typography variant="caption" sx={{ fontWeight: 850 }}>Actividad total</Typography></Stack>
      </Stack>
    </Box>
  );
}


export function ProductVisitsChart({ rows = [], compact = false }) {
  const totalViews = rows.reduce((sum, row) => sum + safeNumber(row.productViews), 0);
  if (!rows.length || totalViews <= 0) {
    return <Typography variant="body2" sx={{ color: WEB_STATS_BRAND.muted }}>Todavía no hay visitas a fichas de productos o servicios para graficar.</Typography>;
  }

  const width = compact ? 560 : 920;
  const height = compact ? 220 : 270;
  const left = compact ? 12 : 28;
  const right = 12;
  const top = 18;
  const bottom = compact ? 34 : 40;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const average = totalViews / Math.max(1, rows.length);
  const max = Math.max(1, average, ...rows.map((row) => safeNumber(row.productViews)));
  const x = (index) => left + (rows.length === 1 ? chartWidth / 2 : (index / (rows.length - 1)) * chartWidth);
  const y = (value) => top + chartHeight - (safeNumber(value) / max) * chartHeight;
  const points = rows.map((row, index) => `${x(index)},${y(row.productViews)}`).join(" ");
  const areaPoints = `${left},${top + chartHeight} ${points} ${width - right},${top + chartHeight}`;
  const labelIndexes = [...new Set([0, Math.floor((rows.length - 1) / 2), rows.length - 1])];
  const pointIndexes = rows.length <= 31 ? rows.map((_, index) => index) : labelIndexes;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box sx={{ width: "100%", overflowX: compact ? "auto" : "hidden", WebkitOverflowScrolling: "touch", overscrollBehaviorX: "contain", pb: compact ? .35 : 0 }}>
        <Box sx={{ minWidth: compact ? { xs: 460, sm: 560 } : 0 }}>
          <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Evolución de visitas a productos y servicios">
        {[0, .25, .5, .75, 1].map((ratio) => {
          const lineY = top + chartHeight * ratio;
          return <line key={ratio} x1={left} x2={width - right} y1={lineY} y2={lineY} stroke="rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" strokeWidth="1" />;
        })}
        <polygon points={areaPoints} fill="rgba(243,90,58,.10)" />
        <line x1={left} x2={width - right} y1={y(average)} y2={y(average)} stroke={WEB_STATS_BRAND.purple} strokeWidth="2" strokeDasharray="7 7" opacity=".85" />
        <polyline points={points} fill="none" stroke={WEB_STATS_BRAND.orange} strokeWidth={compact ? 3 : 4} strokeLinecap="round" strokeLinejoin="round" />
        {pointIndexes.map((index) => (
          <circle key={`product-view-${rows[index]?.date}-${index}`} cx={x(index)} cy={y(rows[index]?.productViews)} r={compact ? 3 : 4} fill={WEB_STATS_BRAND.orange} />
        ))}
        {labelIndexes.map((index) => (
          <text key={index} x={x(index)} y={height - 8} textAnchor="middle" fontSize={compact ? 18 : 15} fill="rgba(var(--wp-panel-ink-rgb, 25,36,47),.55)">
            {formatDate(rows[index]?.date)}
          </text>
        ))}
          </svg>
        </Box>
      </Box>
      <Stack direction="row" spacing={compact ? 1.2 : 2} justifyContent="center" useFlexGap flexWrap="wrap" sx={{ mt: compact ? .1 : -.5 }}>
        <Stack direction="row" spacing={.55} alignItems="center"><Box sx={{ width: 9, height: 9, borderRadius: 999, bgcolor: WEB_STATS_BRAND.orange }} /><Typography variant="caption" sx={{ fontWeight: 850 }}>Visitas</Typography></Stack>
        <Stack direction="row" spacing={.55} alignItems="center"><Box sx={{ width: 16, borderTop: `2px dashed ${WEB_STATS_BRAND.purple}` }} /><Typography variant="caption" sx={{ fontWeight: 850 }}>Promedio {formatNumber(average, 1)}/día</Typography></Stack>
      </Stack>
    </Box>
  );
}

export function ActionBarsChart({ rows = [], compact = false, limit = 7 }) {
  const visibleRows = rows.slice(0, limit);
  if (!visibleRows.length) {
    return <Typography variant="body2" sx={{ color: WEB_STATS_BRAND.muted }}>Todavía no hay acciones suficientes para graficar.</Typography>;
  }
  const max = Math.max(1, ...visibleRows.map((row) => safeNumber(row.count)));

  return (
    <Stack spacing={compact ? 1 : 1.25}>
      {visibleRows.map((row, index) => (
        <Box key={`${row.action}-${row.category}-${row.source}-${index}`}>
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="baseline">
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ color: WEB_STATS_BRAND.dark, fontWeight: 900, fontSize: compact ? 13 : 14 }}>{row.label || row.action || "Actividad"}</Typography>
              <Typography noWrap variant="caption" sx={{ display: "block", color: WEB_STATS_BRAND.muted }}>{categoryLabel(row.category)} · {sourceLabel(row.source)}</Typography>
            </Box>
            <Typography sx={{ color: WEB_STATS_BRAND.dark, fontWeight: 950, fontSize: compact ? 14 : 16, flexShrink: 0 }}>{formatNumber(row.count)}</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={(safeNumber(row.count) / max) * 100}
            sx={{ mt: .55, height: compact ? 7 : 9, borderRadius: 999, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)", "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: index === 0 ? WEB_STATS_BRAND.orange : WEB_STATS_BRAND.blue } }}
          />
        </Box>
      ))}
    </Stack>
  );
}

export function BreakdownRow({ label, value, total, helper, compact = false, color = WEB_STATS_BRAND.orange }) {
  const percent = total > 0 ? Math.min(100, (safeNumber(value) / total) * 100) : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="baseline">
        <Typography sx={{ color: WEB_STATS_BRAND.dark, fontWeight: 900, fontSize: compact ? 13 : 15 }}>{label}</Typography>
        <Typography sx={{ color: WEB_STATS_BRAND.dark, fontWeight: 950, fontSize: compact ? 14 : 16 }}>{formatNumber(value)}</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{ mt: .65, height: compact ? 7 : 9, borderRadius: 999, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)", "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: color } }}
      />
      {helper ? <Typography variant="caption" sx={{ display: "block", mt: .45, color: WEB_STATS_BRAND.muted }}>{helper}</Typography> : null}
    </Box>
  );
}
